import { throwIfAborted } from '../abort.js';
import { decimalToUnits } from '../amount/money.js';
import { bankFundingContract } from '../bank-transfers/types.js';
import { fetchFundingQuote } from '../checkout/fetch-funding-quote.js';
import { fundingMethodArtwork } from '../discovery/method-presentation.js';
import { fundingSettlement } from '../discovery/settlement-asset.js';
import { configuredFundingMethods } from '../discovery/unavailable-methods.js';
import { onrampManifest } from '../generated/manifest.js';
import { payoutMethods, withdrawalArtwork } from '../offramp/types.js';
export function accountFundingMethods(connection) {
    const discovery = connection.quoteContext;
    return configuredFundingMethods(onrampManifest).map((method) => {
        const requiresConnection = discovery?.connectionRequiredMethods?.includes(method) ||
            (connection.guest && method !== 'apple_pay' && method !== 'google_pay') ||
            (!connection.guest && !connection.verification && discovery?.methods.includes(method));
        const unavailable = discovery?.unavailableMethods?.find((entry) => entry.method === method) ??
            discovery?.unavailableBankMethods?.find((entry) => entry.method === method);
        return {
            id: method,
            artwork: fundingMethodArtwork[method],
            available: Boolean(discovery?.methods.includes(method) && !requiresConnection),
            reason: unavailable?.reason === 'region_unavailable' ||
                unavailable?.reason === 'asset_unavailable' ||
                unavailable?.reason === 'currency_unavailable' ||
                unavailable?.reason === 'device_unavailable'
                ? unavailable.reason
                : requiresConnection
                    ? 'connection_required'
                    : (unavailable?.reason ??
                        (discovery?.methods.includes(method) ? null : 'provider_unavailable')),
        };
    });
}
export function accountWithdrawalMethods(context) {
    return payoutMethods.map((method) => ({
        id: method,
        artwork: withdrawalArtwork[method],
        available: context?.methods.some((entry) => entry.method === method) ?? false,
        reason: context?.methods.some((entry) => entry.method === method)
            ? null
            : 'provider_unavailable',
    }));
}
/** An allowance inspection never creates an operation, card, or payment attempt. */
export async function readAccountMethodLimits(options, signal, refreshedContext) {
    const { client, provider, connection, method } = options;
    const current = connection.quoteContext;
    if (!accountFundingMethods(connection).some((entry) => entry.id === method && entry.available) ||
        !current)
        throw new Error('onramp_method_limits_unavailable');
    const context = refreshedContext ?? (await client.getContext(signal));
    throwIfAborted(signal);
    if (context.destination !== current.context.destination ||
        context.asset !== current.context.asset ||
        context.country !== current.context.country ||
        context.subdivision !== current.context.subdivision)
        throw new Error('onramp_profile_quote_context_changed');
    if (!context.destination)
        throw new Error('onramp_profile_quote_context_changed');
    const currency = bankFundingContract(method)?.currency ?? options.currency;
    const decimals = bankFundingContract(method) ? 2 : options.decimals;
    const card = method === 'card' ? current.savedCards?.find((entry) => entry.availability.active) : undefined;
    if (method === 'card' && !card)
        return { currency, decimals, limits: [], evidence: 'card_required' };
    const intent = {
        method,
        destination: context.destination,
        settlement: fundingSettlement(context.asset),
        fiatCurrency: currency,
        fiatDecimals: decimals,
        principalUnits: decimalToUnits('50', decimals).toString(),
        ...(card ? { providerPaymentMethodId: card.id } : {}),
    };
    const { review } = await fetchFundingQuote(provider, intent, signal);
    return {
        currency,
        decimals,
        limits: review.limits,
        evidence: review.limits.length ? 'quote' : 'not_supplied',
    };
}
